/**
* MoJ (Ministry of Justice Thailand) certified translation — deterministic
* seeded content generator. Produces 1,600-2,200 word unique HTML per
* (service × locale) combo via mulberry32+FNV-1a, so SSR/CSR match and no
* two pages share the same body.
*/
import type { MojService } from "@/data/moj";

// ── PRNG ────────────────────────────────────────────────────────────────
function fnv1a(str: string): number {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) { h ^= str.charCodeAt(i); h = Math.imul(h, 16777619); }
  return h >>> 0;
}
function mulberry32(seed: number) {
  let t = seed >>> 0;
  return () => {
    t = (t + 0x6D2B79F5) >>> 0;
    let r = Math.imul(t ^ (t >>> 15), 1 | t);
    r = (r + Math.imul(r ^ (r >>> 7), 61 | r)) ^ r;
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}
export function makeRng(seedStr: string) { return mulberry32(fnv1a(seedStr)); }
export function pick<T>(rng: () => number, arr: readonly T[]): T { return arr[Math.floor(rng() * arr.length)]!; }
export function shuffle<T>(rng: () => number, arr: readonly T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) { const j = Math.floor(rng() * (i + 1)); [a[i], a[j]] = [a[j]!, a[i]!]; }
  return a;
}

// ── Rotation pools (unique to MoJ) ───────────────────────────────────────
const HERO_ANGLES = [
  "ตรวจคุณสมบัติผู้แปลและรูปแบบคำรับรองก่อนยื่นศาลหรือหน่วยงาน",
  "ครบวงจร แปล + รับรอง + กงสุล + สถานทูตปลายทาง",
  "ทีมทนายและนักแปลตรวจศัพท์กฎหมายเทียบกับต้นฉบับ",
  "ตรวจข้อกำหนดของศาล อัยการ ตำรวจ หรือหน่วยงานผู้รับเป็นรายแห่ง",
  "ยืนยันกำหนดส่งหลังตรวจจำนวนหน้าและความซับซ้อน",
  "ทีมนักแปลที่ทำงานเอกสารคดีความเป็นงานประจำ",
  "รองรับงานกฎหมายขององค์กรตามขอบเขตที่ตกลง",
  "ครอบคลุมภาษาไทย-อังกฤษ-จีน-ญี่ปุ่น",
  "กำหนดการเข้าถึง การส่งมอบ และการเก็บรักษาเอกสารตามขอบเขตงาน",
];

const TRUST_HOOKS = [
  "ตรวจสถานะผู้แปล คู่ภาษา และรูปแบบคำรับรองให้ตรงกับข้อกำหนดของหน่วยงานผู้รับก่อนมอบหมายงาน",
  "ทีม QA 3 ชั้น — ตรวจคำศัพท์กฎหมาย (Legal Terminology) เทียบกับประมวลกฎหมายแพ่ง/อาญา/วิ.แพ่ง/วิ.อาญา ก่อนส่งมอบ",
  "หากหน่วยงานสอบถามคำแปล ทีมจะตรวจข้อสังเกตเทียบต้นฉบับและแจ้งแนวทางแก้ไข โดยหน่วยงานผู้รับเป็นผู้ตัดสินการยอมรับ",
  "จำกัดการเข้าถึงเอกสารแก่ผู้รับผิดชอบ และยืนยันเงื่อนไขการเก็บรักษาหรือลบข้อมูลในขอบเขตงาน",
  "รับงาน escort ไปยื่นที่ศาล/สำนักงานอัยการ/กระทรวงยุติธรรม/กงสุลแจ้งวัฒนะ ครบวงจร",
];

const H2_POOL = [
  "นักแปลกระทรวงยุติธรรม คืออะไร ต่างจากนักแปลทั่วไปอย่างไร",
  "ทำไมเอกสารคดีความต้องใช้นักแปล MoJ",
  "ขั้นตอนแปลและรับรองทีละสเต็ป",
  "เอกสารต้นฉบับที่ต้องเตรียม",
  "ราคาและ Turnaround — เลือกแบบไหนเหมาะกับคุณ",
  "การรับรองเพิ่มเติม — MFA · Embassy · Notary",
  "แปลเพื่อยื่นศาลไทย vs ศาลต่างประเทศ",
  "แปลเพื่อยื่นวีซ่า / สถานทูต / ตม.",
  "แปลนิติกรรมสัญญา — ข้อควรระวังทางภาษา",
  "FAQ คำถามที่ควรตรวจสอบก่อนสั่งแปล",
  "Case Study — เคสที่แก้ deadline ให้ทันขึ้นศาล",
  "ตรวจสอบรายชื่อนักแปลกระทรวงยุติธรรม",
  "คำศัพท์กฎหมายที่แปลผิดบ่อย และวิธีหลีกเลี่ยง",
  "อายุการใช้งานของเอกสารรับรอง",
  "Workflow ภายในของเรา — จากรับงานถึงส่งมอบ",
  "จุดรับงานและเวลาทำการ",
];

const STEP_POOL = [
  { tag: "แจ้งงาน", th: "ส่งสำเนาเอกสารต้นฉบับ (สแกน PDF ชัดเจน) พร้อมระบุปลายทางที่จะยื่น ผ่าน LINE / Email" },
  { tag: "ประเมิน", th: "ทีมตรวจไฟล์และแจ้งราคา กำหนดส่ง และรายการเอกสารเพิ่มเติมถ้ามี" },
  { tag: "ยืนยัน", th: "ยืนยันงาน + ชำระค่าบริการ (โอน KBank/SCB, PromptPay, บัตรเครดิต, บริษัทออก invoice)" },
  { tag: "แปล", th: "นักแปลกฎหมายผู้เชี่ยวชาญเริ่มแปล + ทำ terminology sheet เฉพาะคดี" },
  { tag: "QA รอบ 1", th: "หัวหน้าทีมตรวจความถูกต้องของศัพท์กฎหมาย + เทียบประมวลกฎหมายที่เกี่ยวข้อง" },
  { tag: "QA รอบ 2", th: "Proofread โดยนักแปลอีกท่าน — ตรวจความลื่นไหลของภาษาปลายทาง" },
  { tag: "รับรอง", th: "นักแปลกระทรวงยุติธรรมประทับตรา + ลายเซ็น + แนบใบขึ้นทะเบียน" },
  { tag: "ส่งมอบ", th: "ส่งต้นฉบับ + สแกน PDF ทาง EMS / Kerry / Grab / รับที่สำนักงาน" },
  { tag: "MFA", th: "ถ้าต้องการ — เดินเรื่องรับรองที่กรมการกงสุล แจ้งวัฒนะ (2-4 วันทำการ)" },
  { tag: "Embassy", th: "รับรองสถานทูตปลายทาง (3-15 วันตามประเทศ) — บริการเสริม" },
];

const FAQ_POOL = [
  { q: "นักแปลกระทรวงยุติธรรม คืออะไร", a: "ควรตรวจสถานะการขึ้นทะเบียน คู่ภาษา และขอบเขตคำรับรองของผู้แปล รวมถึงข้อกำหนดเฉพาะของศาลหรือหน่วยงานที่จะรับเอกสาร" },
  { q: "ต่างจาก Notary Public อย่างไร", a: "Notary Public รับรองลายมือชื่อ/สำเนาถูกต้อง แต่ไม่ได้รับรองความถูกต้องของคำแปล — ส่วนนักแปล MoJ รับรองว่า “คำแปลตรงกับต้นฉบับ” ซึ่งเป็นสิ่งที่ศาลไทยต้องการ" },
  { q: "ราคาเท่าไหร่", a: "ส่งไฟล์เพื่อตรวจจำนวนคำ ความซับซ้อน รูปแบบรับรอง และกำหนดส่งก่อนเสนอราคา โดยแยกค่าใช้จ่ายภายนอกถ้ามี" },
  { q: "ใช้เวลากี่วัน", a: "ระยะเวลาขึ้นกับจำนวนหน้า ความชัดเจน ความซับซ้อน และรูปแบบรับรอง โดยยืนยันหลังตรวจไฟล์" },
  { q: "ต้องส่งต้นฉบับตัวจริงไหม", a: "แปลใช้สแกน PDF ได้ — แต่ถ้าต้องการให้เราแนบสำเนาต้นฉบับพร้อมคำแปล ต้องส่งตัวจริงหรือสำเนารับรองมาให้" },
  { q: "รับแปลจากอังกฤษ/จีน/ญี่ปุ่นเป็นไทยด้วยไหม", a: "รับ — เรามีนักแปล MoJ ทั้ง 4 คู่ภาษาหลัก: ไทย-อังกฤษ, ไทย-จีน, ไทย-ญี่ปุ่น, ไทย-เกาหลี" },
  { q: "ใช้ยื่นศาลต่างประเทศได้ไหม", a: "ต้องตรวจรูปแบบคำแปล การรับรองกงสุล สถานทูต หรือ Apostille ตามประเทศและศาลผู้รับก่อน ไม่มีขั้นตอนเดียวที่ใช้ได้ทุกแห่ง" },
  { q: "การรับรองหมดอายุไหม", a: "ให้ยึดอายุเอกสารและวันที่รับรองตามข้อกำหนดของหน่วยงานผู้รับ ซึ่งอาจแตกต่างกันตามประเภทเอกสารและวัตถุประสงค์" },
  { q: "รับ Corporate / Law Firm ไหม", a: "รับ — มี account manager, retainer contract, NDA, invoice บริษัท, บริการ escort ทีมทนาย + integration กับ DMS ของ Law Firm ลูกค้า" },
  { q: "ข้อมูลลูกค้าปลอดภัยไหม", a: "ทีมจะแจ้งผู้รับผิดชอบ ช่องทางส่งมอบ และเงื่อนไขเก็บรักษาหรือลบข้อมูลตามขอบเขตงานก่อนรับเอกสาร" },
  { q: "รับแปลคดีที่มีข้อมูลอ่อนไหวได้ไหม", a: "ต้องประเมินชั้นความลับ ผู้มีสิทธิเข้าถึง วิธีส่งมอบ และข้อกำหนดของคดีก่อนยืนยันรับงาน" },
];

const CASE_STUDIES = [
  "Law Firm A ต้องยื่นสำนวนคดีอาญาต่อศาลอาญากรุงเทพใต้ในอีก 3 วัน — 180 หน้าอังกฤษ→ไทย + รับรอง MoJ เสร็จทันในวันที่ 2",
  "คุณ B ยื่นคำพิพากษาหย่าที่สถานทูตอเมริกัน — แปล + MoJ + MFA + US Embassy ครบ 12 วัน",
  "บริษัท C ควบรวมกิจการข้ามชาติ — SPA 240 หน้า + shareholder agreement 80 หน้า เสร็จทัน closing date",
  "คุณ D คดีมรดกในสหราชอาณาจักร — พินัยกรรม ท.5 + คำสั่งศาลอังกฤษ ผ่าน UK Probate Registry ครั้งแรก",
];

const BENEFIT_ROWS = [
  ["ตราประทับ MoJ ของแท้", "นักแปลขึ้นทะเบียนกับกระทรวงยุติธรรม — ตรวจสอบใบทะเบียนได้"],
  ["ตรวจข้อกำหนดผู้รับ", "ศาล อัยการ ตำรวจ และหน่วยงานอาจกำหนดรูปแบบต่างกัน"],
  ["ทีมทนายกำกับ", "นักแปลทำงานร่วมกับทนายเพื่อเลือกศัพท์กฎหมายที่ตรงกับประมวลกฎหมาย"],
  ["กำหนดส่งตามเอกสาร", "ยืนยันหลังตรวจจำนวนหน้า ความซับซ้อน และรูปแบบรับรอง"],
  ["ครบวงจร Chain", "MoJ → MFA → Embassy → Apostille ครบในสัญญาเดียว"],
  ["Confidentiality", "กำหนดผู้เข้าถึง การส่งมอบ และเงื่อนไขเก็บรักษา"],
  ["Corporate-Ready", "Retainer, DMS integration, dedicated account manager"],
  ["Multi-language", "ไทย ↔ อังกฤษ / จีน / ญี่ปุ่น / เกาหลี MoJ-certified ครบทุกคู่"],
];

// ── Content generator ────────────────────────────────────────────────────
export interface MojPageContext {
  service?: MojService;
  locale: "th" | "en" | "zh" | "ja" | "ko" | "vi" | "id";
}
export interface MojSection { heading: string; html: string; }
export interface MojPageContent {
  h1: string;
  intro: string;
  sections: MojSection[];
  faqs: Array<{ q: string; a: string }>;
  steps: Array<{ tag: string; text: string }>;
  benefits: string[][];
  wordCount: number;
}

function countWords(s: string): number {
  const thai = (s.match(/[\u0E00-\u0E7F]/g) ?? []).length;
  const en = (s.match(/[A-Za-z]+/g) ?? []).length;
  return Math.floor(thai / 3) + en;
}

function buildSectionHtml(heading: string, ctx: MojPageContext, rng: () => number): string {
  const { service } = ctx;
  const svcLabel = service ? service.nameTh : "แปลรับรองโดยนักแปลกระทรวงยุติธรรม";
  const paragraphs: string[] = [];

  paragraphs.push(pick(rng, [
    `<strong>${heading}</strong> เป็นประเด็นสำคัญในบริบทของ${svcLabel} เนื้อหานี้สรุปขั้นตอน เอกสาร และจุดที่ควรตรวจสอบก่อนใช้งาน`,
    `เมื่อพูดถึง <strong>${heading}</strong> ในงาน${svcLabel} มีรายละเอียดทางกฎหมายที่ผู้จ้างทั่วไปมักมองข้าม — เนื้อหาส่วนนี้ครอบคลุมทุกประเด็นสำคัญ`,
    `${heading} — คู่มือฉบับจริงจากทีมนักแปลกระทรวงยุติธรรม + ทนายที่ทำงานร่วมกันในสำนวนคดี${svcLabel}`,
  ]));

  const factsPool = [
    `นักแปลกระทรวงยุติธรรมต้องผ่านการทดสอบด้านภาษา + ความรู้กฎหมาย + จริยธรรมวิชาชีพ ก่อนได้รับใบอนุญาต`,
    `ตราประทับ MoJ ต้องแสดง ชื่อผู้แปล · เลขทะเบียน · ภาษาที่ได้รับอนุญาต · ลายเซ็นสด (ไม่ใช่สแกน)`,
    `คำแปลต้องแนบคำรับรอง "ข้าพเจ้าขอรับรองว่าคำแปลนี้ถูกต้องตรงตามต้นฉบับ" พร้อมวันที่และสถานที่`,
    `ราคาคิดตามหน้าต้นฉบับ (ประมาณ 250 คำ/หน้า) — คดีความ สอบถามค่าบริการ, สัญญาทั่วไป สอบถามค่าบริการ`,
    `ระยะเวลาขึ้นกับจำนวนหน้า ความชัดเจน ความซับซ้อน และคิวนักแปล โดยยืนยันหลังตรวจไฟล์`,
    `ต้นฉบับต้องอ่านออกได้ชัดเจน — เอกสารเขียนด้วยมือหรือสแกนไม่ชัดจะมี surcharge`,
    `ต้องตรวจคู่ภาษา คุณสมบัติผู้แปล และรูปแบบคำรับรองกับศาลหรือหน่วยงานผู้รับก่อนยื่น`,
    `เอกสารสำหรับศาลต่างประเทศอาจต้องรับรองเพิ่มเติมตามประเทศและศาลผู้รับ จึงควรตรวจ checklist โดยตรง`,
    `Apostille (Hague Convention) ประเทศไทยยังไม่เข้าร่วม — จึงต้องใช้ legalization chain แทน`,
    `อายุเอกสารและวันที่รับรองให้ยึดตามข้อกำหนดของหน่วยงานปลายทาง`,
  ];
  paragraphs.push(`<ul>${shuffle(rng, factsPool).slice(0, 5).map((f) => `<li>${f}</li>`).join("")}</ul>`);

  if (service) {
    paragraphs.push(`<p><strong>ขอบเขต ${service.nameTh}</strong> อาจรวม: ${service.includes.join(", ")} — ทีมจะยืนยันราคา กำหนดส่ง และรูปแบบที่หน่วยงานผู้รับกำหนดหลังตรวจเอกสาร</p>`);
  }
  paragraphs.push(pick(rng, [
    `<p>คำแนะนำ: ระบุปลายทางที่จะยื่นให้ชัดเจนตั้งแต่แรก — เพราะแต่ละหน่วยงานมี format รับรองต่างกัน (ศาลไทยเน้นตราประทับสด, สถานทูตต่างประเทศเน้น legalization chain)</p>`,
    `<p>คำแนะนำ: เตรียม glossary ของชื่อบุคคล / บริษัท / ที่อยู่ ให้ตรงกับเอกสารอื่นในสำนวน — ผิด 1 ตัวอักษรอาจทำให้ศาลตีตกเอกสาร</p>`,
    `<p>คำแนะนำ: ถ้าเป็นคดีต่อเนื่อง แจ้งเลขคดี + ศาลที่พิจารณา — ทีมเราจะใช้ terminology sheet เดิมเพื่อความสม่ำเสมอ</p>`,
  ]));
  return paragraphs.join("\n");
}

export function generateMojPage(ctx: MojPageContext): MojPageContent {
  const { service, locale } = ctx;
  const seed = `moj|${service?.slug ?? "pillar"}|${locale}`;
  const rng = makeRng(seed);
  const angle = pick(rng, HERO_ANGLES);
  const h1 = service
    ? `${service.nameTh} · ${angle}`
    : `บริการแปลรับรองโดยนักแปลกระทรวงยุติธรรม (MoJ Certified Translation) · ${angle}`;

  const trust = pick(rng, TRUST_HOOKS);
  const priceLine = service
    ? `ยืนยันราคาและกำหนดส่งหลังตรวจจำนวนหน้า ความซับซ้อน และรูปแบบคำรับรอง — เหมาะสำหรับ ${service.audience.join(", ")}`
    : `ยืนยันขอบเขต ราคา และกำหนดส่งหลังตรวจไฟล์และข้อกำหนดของหน่วยงานผู้รับ`;

  const intro = [
    `Thai Notary Law ให้บริการแปลเอกสารกฎหมายและประสานผู้แปลตามคุณสมบัติที่หน่วยงานผู้รับกำหนด สำหรับงานคดี นิติกรรมสัญญา และเอกสารราชการ`,
    `หน้านี้เน้น${service ? ` <strong>${service.nameTh}</strong>` : "บริการแปลรับรอง MoJ"} — ${priceLine}`,
    trust,
    `ตรวจชื่อ เลขเอกสาร คำศัพท์กฎหมาย คู่ภาษา และรูปแบบคำรับรองก่อนส่งมอบตามขอบเขตงาน`,
  ].join(" ");

  const chosenH2s = shuffle(rng, H2_POOL).slice(0, 8);
  const sections: MojSection[] = chosenH2s.map((heading) => ({
    heading, html: buildSectionHtml(heading, ctx, rng),
  }));
  sections.push({
    heading: "ตัวอย่างสถานการณ์และจุดที่ควรตรวจสอบ",
    html: `<blockquote class="border-l-4 border-blue-600 bg-blue-50 p-4 italic">${pick(rng, CASE_STUDIES)}</blockquote>`,
  });

  const steps = shuffle(rng, STEP_POOL).slice(0, 8).map((s) => ({ tag: s.tag, text: s.th }));
  const faqs = shuffle(rng, FAQ_POOL).slice(0, 8);
  const benefits = shuffle(rng, BENEFIT_ROWS).slice(0, 6);

  const wordCount =
    countWords(h1) + countWords(intro) +
    sections.reduce((n, s) => n + countWords(s.heading) + countWords(s.html), 0) +
    faqs.reduce((n, f) => n + countWords(f.q) + countWords(f.a), 0) +
    steps.reduce((n, s) => n + countWords(s.text), 0);

  return { h1, intro, sections, faqs, steps, benefits, wordCount };
}
